import React, { useEffect, useMemo, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { LossFrameDemandBarCard } from "../stage2/Stage2ResultStage";
import { InitialOfferExplanationTabs } from "./InitialOfferExplanationTabs";
import { DemandAgainstPotChart } from "./NegotiationDonutCharts";
import {
  getCounterpartCopy,
  getNegotiationTermCopy,
  isLossFrameDesign,
} from "../payoffFrame";

export function NegotiationDiscussionStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();

  const negotiation = game.get("negotiation") || {};
  const preDiscussionAgreement = negotiation.preDiscussionAgreement || {};
  const initialDollarAsksByPlayerId = negotiation.initialDollarAsksByPlayerId || {};
  const initialExplanationsByPlayerId = negotiation.initialExplanationsByPlayerId || {};
  const pieDollars = Math.max(0, Number(negotiation.pieDollars ?? 0));
  const maxOfferDollars = Number(pieDollars.toFixed(2));
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const termCopy = getNegotiationTermCopy(design);
  const counterpart = getCounterpartCopy(design);
  const testMode = design.testMode === true;

  const isClearedByInitialOffers = preDiscussionAgreement.status === "agreed";
  const partner = players.find((p) => p.id !== player.id);
  const partnerId = partner?.id;

  const initialDraft = Number(player.get("negotiationDraftOfferDollars"));
  const [offerInput, setOfferInput] = useState(
    Number.isFinite(initialDraft) ? initialDraft.toFixed(2) : "0.00"
  );

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  useEffect(() => {
    if (!isClearedByInitialOffers) {
      return;
    }

    player.stage.set("submit", true);
  }, [isClearedByInitialOffers, player]);

  function handleOfferChange(nextValue) {
    if (nextValue === "") {
      setOfferInput("");
      player.stage.set("discussionOfferDraftDollars", 0);
      player.set("negotiationDraftOfferDollars", 0);
      return;
    }

    const numeric = Number(nextValue);
    if (!Number.isFinite(numeric)) {
      return;
    }

    const clamped = Math.max(0, Math.min(numeric, maxOfferDollars));
    setOfferInput(clamped.toFixed(2));
    player.stage.set("discussionOfferDraftDollars", clamped);
    player.set("negotiationDraftOfferDollars", clamped);
  }

  function handleForceEndDiscussion() {
    player.stage.set("submit", true);
  }

  if (isClearedByInitialOffers) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-6 space-y-4">
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          {lossFrame
            ? `Your initial ${termCopy.pluralTerm} fit the total savings, so discussion is skipped.`
            : `Your initial ${termCopy.pluralTerm} fit the total winnings, so discussion is skipped.`}
        </div>
        {!allSubmitted ? (
          <div className="text-sm text-gray-500">
            Waiting for {counterpart.term}…
          </div>
        ) : null}
      </div>
    );
  }

  const alreadySubmitted = Boolean(player.stage.get("submit"));
  const yourInitialDemand = Number(initialDollarAsksByPlayerId?.[player.id] ?? 0);
  const partnerInitialDemand = Number(initialDollarAsksByPlayerId?.[partnerId] ?? 0);

  return (
    <div className="w-full min-w-0 max-w-4xl mx-auto px-8 py-6 space-y-6">
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-4xl font-semibold text-gray-900">
            Discussion
          </h2>
          <p className="text-xl text-gray-600">Chat window is on the right.</p>
        </div>
        {lossFrame ? (
          <div className="negotiation-responsive-card-grid">
            <LossFrameDemandBarCard
              title={termCopy.yourLabel}
              actorLabel="You"
              demand={yourInitialDemand}
              savingsAtStakeDollars={pieDollars}
              demandLabel={termCopy.savingsLabel}
              demandActionPast={termCopy.pastTense}
              demandPercentLabel={termCopy.savingsPercentLabel}
            />
            <LossFrameDemandBarCard
              title={termCopy.counterpartLabel}
              actorLabel={counterpart.capitalizedTerm}
              demand={partnerInitialDemand}
              savingsAtStakeDollars={pieDollars}
              demandLabel={termCopy.savingsLabel}
              demandActionPast={termCopy.pastTense}
              demandPercentLabel={termCopy.savingsPercentLabel}
            />
          </div>
        ) : (
          <div className="negotiation-responsive-card-grid">
            <DemandAgainstPotChart
              title={termCopy.yourLabel}
              demand={yourInitialDemand}
              pot={pieDollars}
              color="#818cf8"
              demandLabel={termCopy.yourLabel}
              remainingLabel="Pot Left Over"
              overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
              totalLabel="pot"
              responsive
              legendTextClassName="text-2xl"
              centerValueClassName="fill-blue-700 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
              chartOrderLabels={[
                "Pot Left Over",
                termCopy.yourLabel,
                `${termCopy.capitalizedTerm} Above Pot`,
              ]}
            />
            <DemandAgainstPotChart
              title={termCopy.counterpartLabel}
              demand={partnerInitialDemand}
              pot={pieDollars}
              color="#f87171"
              demandLabel={termCopy.counterpartLabel}
              remainingLabel="Pot Left Over"
              overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
              totalLabel="pot"
              responsive
              legendTextClassName="text-2xl"
              centerValueClassName="fill-red-700 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
            />
          </div>
        )}
      </div>

      <InitialOfferExplanationTabs
        counterpart={counterpart}
        termCopy={termCopy}
        yourExplanation={initialExplanationsByPlayerId[player.id]}
        partnerExplanation={initialExplanationsByPlayerId[partnerId]}
      />

      {/* <div className="rounded-md border border-gray-200 p-4 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Discussion</h2>
        <p className="text-sm text-gray-700">
          Use chat to negotiate. You can update your offer throughout discussion.
        </p>
        <label className="block text-sm font-medium text-gray-900">
          Your current offer ($):
        </label>
        <input
          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 sm:text-sm"
          type="number"
          step="0.01"
          min="0"
          max={maxOfferDollars}
          value={offerInput}
          onChange={(e) => handleOfferChange(e.target.value)}
          disabled={alreadySubmitted}
        />
      </div> */}

      {testMode ? (
        <div className="flex items-center space-x-3">
          <Button
            type="button"
            disabled={alreadySubmitted}
            handleClick={handleForceEndDiscussion}
          >
            End discussion now (test)
          </Button>
          {alreadySubmitted && !allSubmitted ? (
            <span className="text-sm text-gray-500">
              Waiting for {counterpart.term}…
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
